import { useState, useEffect, useCallback, useRef } from "react";
import { Skeleton, Empty, Table, Pagination } from "antd";
import { ReloadOutlined, WarningOutlined } from "@ant-design/icons";

/**
 * The pieces every 360° account panel is built from.
 *
 * Eight tabs that all fetch one endpoint, page through rows and show the same
 * three states. Written once here so a panel is a column list and a header —
 * and so "loading", "empty" and "failed" look the same wherever you land.
 */

export const cardStyle = {
  background: "#fff",
  borderRadius: 14,
  boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06)",
  border: "1px solid rgba(0,0,0,0.06)",
  overflow: "hidden",
};

// ── Formatting ──────────────────────────────────────────────────────────────

/** Money, with every paisa the value actually carries and none it does not. */
export const inr = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  const paise = Math.abs(value % 1) > 0;
  return `₹${value.toLocaleString("en-IN", {
    minimumFractionDigits: paise ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

export const num = (n) => {
  const value = Number(n);
  return Number.isFinite(value) ? value.toLocaleString("en-IN") : "—";
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const dateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${hh}:${mm}`;
};

export const dateOnly = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/** "2h ago" — the shape people read a last-login in. */
export const relative = (v) => {
  if (!v) return "Never";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return "Just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

/** Milliseconds read as seconds past a second — 340ms stays 340ms. */
export const duration = (ms) => {
  const value = Number(ms);
  if (!Number.isFinite(value)) return "—";
  return value >= 1000 ? `${(value / 1000).toFixed(2)}s` : `${Math.round(value)}ms`;
};

// ── Chips ───────────────────────────────────────────────────────────────────

export const chip = (label, { color = "#475569", bg = "#f1f5f9", border = "#e2e8f0" } = {}) => (
  <span style={{
    display: "inline-block", padding: "2px 10px", borderRadius: 20, fontSize: 11,
    fontWeight: 700, color, background: bg, border: `1px solid ${border}`, whiteSpace: "nowrap",
  }}>
    {label}
  </span>
);

export const TONE = {
  green: { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  red: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  amber: { color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  blue: { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  violet: { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  slate: { color: "#475569", bg: "#f1f5f9", border: "#e2e8f0" },
};

/* Words an API might use for the same outcome. Anything unrecognised keeps its
   own label in a neutral chip rather than being forced into a guess. */
const OK = new Set(["success", "completed", "complete", "active", "done", "paid", "approved"]);
const BAD = new Set(["fail", "failed", "error", "rejected", "cancelled", "canceled", "expired", "revoked"]);
const WARN = new Set(["pending", "processing", "in_progress", "in progress", "review", "draft"]);

export const statusChip = (value) => {
  if (value === null || value === undefined || value === "") return chip("—", TONE.slate);
  const key = String(value).trim().toLowerCase();
  if (OK.has(key)) return chip(titleCase(value), TONE.green);
  if (BAD.has(key)) return chip(titleCase(value), TONE.red);
  if (WARN.has(key)) return chip(titleCase(value), TONE.amber);
  return chip(titleCase(value), TONE.slate);
};

export const titleCase = (s) =>
  String(s ?? "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim() || "—";

// ── Header pieces ───────────────────────────────────────────────────────────

export const StatTile = ({ icon, label, value, sub, color = "#6366f1", bg = "#eef2ff", children }) => (
  /* Full height so a row of these lines up: one card carrying a progress bar or
     a second line of sub-text would otherwise stand taller than its neighbours
     and leave the row looking ragged. */
  <div style={{ ...cardStyle, padding: "18px 20px", display: "flex", gap: 14, alignItems: "flex-start", height: "100%" }}>
    {icon && (
      <div style={{
        width: 42, height: 42, borderRadius: 11, background: bg, color,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0,
      }}>
        {icon}
      </div>
    )}
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", lineHeight: 1.25, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{sub}</div>}
      {children}
    </div>
  </div>
);

/**
 * How much of what was bought has been spent.
 *
 * Capped at 100% on purpose: an account that overran its purchased credits
 * should read as full, not as a bar running past its own track.
 */
export const UsageBar = ({ used, total, color = "#6366f1" }) => {
  const u = Number(used) || 0;
  const t = Number(total) || 0;
  const pct = t > 0 ? Math.min(100, (u / t) * 100) : 0;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ height: 6, borderRadius: 4, background: "#f1f5f9", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 4, transition: "width .3s ease" }} />
      </div>
      <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 5 }}>
        used <b style={{ color: "#334155" }}>{num(u)}</b> of {num(t)} purchased
      </div>
    </div>
  );
};

export const TileGrid = ({ children, min = 220 }) => (
  <div style={{
    display: "grid", gridTemplateColumns: `repeat(auto-fit,minmax(${min}px,1fr))`,
    gap: 16, marginBottom: 20, alignItems: "stretch",
  }}>
    {children}
  </div>
);

export const PanelHeader = ({ icon, title, subtitle, right, iconBg = "linear-gradient(135deg,#6366f1,#4f46e5)" }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 12, padding: "18px 22px",
    borderBottom: "1px solid #f1f5f9", flexWrap: "wrap",
  }}>
    <div style={{
      width: 36, height: 36, borderRadius: 10, background: iconBg, color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 700, fontSize: 15.5, color: "#0f172a", letterSpacing: -0.3 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 1 }}>{subtitle}</div>}
    </div>
    {right}
  </div>
);

// ── States ──────────────────────────────────────────────────────────────────

export const PanelSkeleton = ({ tiles = 0 }) => (
  <>
    {tiles > 0 && (
      <TileGrid>
        {Array.from({ length: tiles }).map((_, i) => (
          <div key={i} style={{ ...cardStyle, padding: "18px 20px" }}>
            <Skeleton active paragraph={{ rows: 1 }} title={{ width: "50%" }} />
          </div>
        ))}
      </TileGrid>
    )}
    <div style={{ ...cardStyle, padding: 22 }}>
      <Skeleton active paragraph={{ rows: 6 }} />
    </div>
  </>
);

/**
 * A failure someone can act on.
 *
 * Named rather than swallowed: a panel that silently shows "no data" after a
 * failed request tells the reader this customer has none, which is a different
 * and wrong answer.
 */
export const PanelError = ({ error, onRetry }) => (
  <div style={{ ...cardStyle, padding: "40px 24px", textAlign: "center" }}>
    <WarningOutlined style={{ fontSize: 28, color: "#f59e0b" }} />
    <div style={{ fontWeight: 700, color: "#0f172a", marginTop: 12, fontSize: 15 }}>
      This could not be loaded
    </div>
    <div style={{ color: "#64748b", fontSize: 13, marginTop: 4, marginBottom: 18 }}>
      {error?.response?.data?.message || error?.message || "Something went wrong."}
    </div>
    <button
      onClick={onRetry}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 18px",
        borderRadius: 10, border: "1px solid #ddd6fe", background: "#f5f3ff",
        color: "#6366f1", fontSize: 13, fontWeight: 600, cursor: "pointer",
      }}
    >
      <ReloadOutlined /> Try again
    </button>
  </div>
);

export const PanelEmpty = ({ message = "Nothing here yet." }) => (
  <div style={{ padding: "44px 20px" }}>
    <Empty description={<span style={{ color: "#94a3b8", fontSize: 13 }}>{message}</span>} />
  </div>
);

// ── Table ───────────────────────────────────────────────────────────────────

/**
 * One table, one pager, the same empty state everywhere.
 *
 * Pagination is the server's: `total` counts every row, not the page, so the
 * pager is driven by the envelope rather than by what happens to be loaded.
 */
export const PanelTable = ({
  columns, rows, loading, page, total, perPage = 20, onPage, empty, rowKey = "id", scrollX = 900,
}) => (
  <>
    <Table
      columns={columns}
      dataSource={rows || []}
      rowKey={(r, i) => r?.[rowKey] ?? `${page}-${i}`}
      pagination={false}
      loading={loading}
      size="small"
      scroll={{ x: scrollX }}
      locale={{ emptyText: <PanelEmpty message={empty} /> }}
      style={{ fontSize: 13 }}
    />
    {total > perPage && (
      <div style={{
        padding: "14px 20px", borderTop: "1px solid #f1f5f9", display: "flex",
        justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap",
        background: "#fafbff",
      }}>
        <span style={{ fontSize: 12.5, color: "#64748b" }}>
          Showing <b>{(page - 1) * perPage + 1}–{Math.min(page * perPage, total)}</b> of <b>{num(total)}</b>
        </span>
        <Pagination
          current={page}
          total={total}
          pageSize={perPage}
          onChange={onPage}
          showSizeChanger={false}
          size="small"
        />
      </div>
    )}
  </>
);

// ── Data loading ────────────────────────────────────────────────────────────

/**
 * Fetch when the tab is first opened, then keep the answer.
 *
 * Eight panels behind one tab bar: loading all of them on mount would fire eight
 * requests for tabs nobody opens. So nothing is fetched until `enabled` turns
 * true, and the result is held after that — flipping between tabs should not
 * re-query.
 *
 * The cache is keyed on the customer. Opening a different profile has to discard
 * the previous one's rows rather than show them under a new name, which is why
 * `userId` resets rather than merely re-fetches.
 */
export const usePanelData = (fetcher, userId, enabled, { paged = true, perPage = 20 } = {}) => {
  const [state, setState] = useState({ data: null, loading: false, error: null });
  const [page, setPage] = useState(1);
  /* Which (customer, page) is on screen, so an answer that arrives after the
     user has moved on is dropped instead of overwriting what they are reading. */
  const wanted = useRef("");

  const load = useCallback(async (toPage = 1) => {
    const key = `${userId}:${toPage}`;
    wanted.current = key;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = paged ? await fetcher(userId, toPage, perPage) : await fetcher(userId);
      if (wanted.current !== key) return;
      setState({ data, loading: false, error: null });
    } catch (error) {
      if (wanted.current !== key) return;
      setState({ data: null, loading: false, error });
    }
  }, [fetcher, userId, paged, perPage]);

  // A different customer invalidates everything this panel is holding.
  useEffect(() => {
    setState({ data: null, loading: false, error: null });
    setPage(1);
  }, [userId]);

  useEffect(() => {
    if (!enabled || !userId) return;
    if (state.data || state.loading || state.error) return;
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, userId]);

  const goToPage = useCallback((next) => {
    setPage(next);
    load(next);
  }, [load]);

  return {
    ...state,
    page,
    perPage,
    goToPage,
    reload: () => load(page),
    rows: state.data?.data || [],
    total: state.data?.total ?? (state.data?.data?.length || 0),
  };
};
